import type { ProcedureType } from '@/types/cgt'

/** The procedure type that produces a kit rather than patient care. */
export const KIT_PROCEDURE_TYPE: ProcedureType = 'KitCreation'

/** A manufacturing run. Its patient, if any, comes from Epic (z8nrz7ckj9). */
export const MES_PROCEDURE_TYPE: ProcedureType = 'MES'

/**
 * Whether the Create procedure form has enough to submit.
 *
 * Pure and shared, because the submit handler and the button's `disabled` were
 * two separate expressions of the same rule and drifted: the kit case was added
 * to the handler only, so the button stayed dead forever — a kit has no patient
 * to satisfy the condition the button still carried. One predicate, one truth.
 *
 * A KIT needs no patient ("Patient = LOT Number") and no assignee — assignment
 * happens afterwards, to execute the run.
 */
export function canSubmitProcedure(input: {
  procedureType: ProcedureType
  procedureTemplateId: string
  patientMrn: string
}): boolean {
  if (!input.procedureTemplateId) return false
  if (input.procedureType === KIT_PROCEDURE_TYPE) return true
  // An MES order has no patient to enter either: it acquires one from an Epic
  // order after the fact, or never (z8nrz7ckj9). Requiring an MRN here would
  // make the button permanently dead, which is the exact bug the kit case above
  // was added to fix.
  if (input.procedureType === MES_PROCEDURE_TYPE) return true
  return !!input.patientMrn
}

/** Mirrors the backend's Max on the create DTOs. */
export const MAX_PROCEDURE_QUANTITY = 10000

/**
 * Ten years, mirroring MAX_DUE_OFFSET_DAYS on the server.
 *
 * A typo guard, not a business rule: an offset in the thousands is a mis-keyed
 * value, and a due date centuries out reads as corrupt data rather than a plan.
 */
export const MAX_DUE_OFFSET_DAYS = 3650

/**
 * Whether the Quantity field holds something submittable.
 *
 * Lives here rather than in the dialog because `vitest.config.ts` only matches
 * `src/**​/*.test.ts` — no `.tsx` — so a rule left inside the component could
 * never be tested. Same reason `canSubmitProcedure` is here.
 *
 * A whole number of at least 1: quantity counts UNITS PRODUCED, and half a
 * transplant is not a thing. The empty string is valid and means "the default",
 * so clearing the box to retype does not flash an error mid-keystroke.
 */
export function isValidProcedureQuantity(raw: string): boolean {
  if (raw.trim() === '') return true
  if (!/^\d+$/.test(raw.trim())) return false
  const n = Number(raw)
  return n >= 1 && n <= MAX_PROCEDURE_QUANTITY
}

/**
 * Whether the inline Qty cell holds something submittable.
 *
 * Differs from `isValidProcedureQuantity` in exactly one way: empty is INVALID
 * here. On the create form a blank box means "use the default"; on an existing
 * row it would mean erasing a number that must always have one, so the cell
 * reverts to the stored value instead of sending anything.
 */
export function isValidProcedureQuantityEdit(raw: string): boolean {
  return raw.trim() !== '' && isValidProcedureQuantity(raw)
}

/** The value to send: `undefined` where the backend's default of 1 applies. */
export function procedureQuantityPayload(raw: string): number | undefined {
  const n = Number(raw.trim())
  return isValidProcedureQuantity(raw) && Number.isInteger(n) && n > 1 ? n : undefined
}

/** Mirrors the backend's MaxLength on the work order field. */
export const MAX_WORK_ORDER_LENGTH = 64

/**
 * Mirrors `WORK_ORDER_PATTERN` in the backend's procedures/work-order.ts.
 *
 * Deliberately NOT the minted `WO-<yy>-<nnnn>` shape. A number typed here comes
 * from a hospital or ERP system with its own scheme, and matching theirs is the
 * whole reason the field is editable — so the rule is only "readable
 * identifier, one line".
 */
const WORK_ORDER_PATTERN = /^[A-Za-z0-9][A-Za-z0-9 \-_/.]*$/

export const WORK_ORDER_FORMAT_MESSAGE =
  'Work order must start with a letter or number and may contain only letters, numbers, spaces and - _ / .'

/** Trim and collapse whitespace, so ` WO 12 ` and `WO 12` are one number. */
export function normalizeWorkOrder(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ')
}

/**
 * Whether the Work Order field holds something submittable.
 *
 * Empty is VALID and means "let the server decide" — mint one on a Transplant,
 * leave it blank otherwise. Without that, the field would force every operator
 * to invent a number just to create a procedure.
 */
export function isValidWorkOrderNumber(raw: string): boolean {
  const value = normalizeWorkOrder(raw)
  if (value === '') return true
  return value.length <= MAX_WORK_ORDER_LENGTH && WORK_ORDER_PATTERN.test(value)
}

/** The value to send on create: `undefined` means "mint it, or leave it blank". */
export function workOrderPayload(raw: string): string | undefined {
  const value = normalizeWorkOrder(raw)
  return isValidWorkOrderNumber(raw) && value !== '' ? value : undefined
}

/**
 * The value to send on an inline EDIT, where the tri-state matters: clearing the
 * box must send explicit `null` to erase the stored number, not `undefined`,
 * which the backend reads as "leave untouched".
 */
export function workOrderUpdatePayload(raw: string): string | null {
  const value = normalizeWorkOrder(raw)
  return value === '' ? null : value
}

/**
 * A field on the Create procedure form that a refusal can be blamed on.
 *
 * `null` where it cannot be: a refusal with no field still has to be shown, it
 * just goes to the dialog-level banner instead of next to an input.
 */
export type ProcedureCreateField =
  | 'patient'
  | 'template'
  | 'scheduledDate'
  | 'dueDate'
  | 'dueOffset'
  | 'quantity'
  | 'workOrder'

/**
 * Which field a SERVER refusal is about (z8nrz7dyc2, AC3.1).
 *
 * The dialog used to print `e.message` — the whole `{"message":[…],"error":
 * "Bad Request","statusCode":400}` envelope — into a banner at the foot of a
 * long form. `requestErrorMessage` unwraps the envelope; this decides where the
 * unwrapped text belongs, so the operator is looking at the input that caused it.
 *
 * Matching on message text is not how you would design this from scratch — the
 * server sends no field key, so text is what there is. It stays honest by
 * failing OPEN: an unrecognised message returns `null` and is shown in the
 * banner, exactly as every message is today. Nothing is ever hidden because a
 * pattern did not match.
 */
export function procedureCreateErrorField(message: string): ProcedureCreateField | null {
  const m = message.toLowerCase()
  // Ordered: the work-order and template rules name their field unambiguously,
  // so they are tested before the looser date wording.
  if (m.includes('work order')) return 'workOrder'
  if (m.includes('proceduretemplate') || m.includes('template version')) return 'template'
  if (m.includes('is inactive')) return 'template'
  if (m.includes('patientmrn') || m.includes('mrn')) return 'patient'
  if (m.includes('quantity')) return 'quantity'
  if (m.includes('duedate') || m.includes('due date')) return 'dueDate'
  if (m.includes('dueoffsetdays') || m.includes('due in')) return 'dueOffset'
  if (m.includes('scheduleddate') || m.includes('scheduled')) return 'scheduledDate'
  return null
}

/** The procedure lists that create runs: patient care (Transplants), Kits, MES, CAPA. */
export type ProcedureListVariant = 'patient' | 'kit' | 'mes' | 'capa'

/**
 * Which templates a list's Create dialog offers.
 *
 * Kits offers kit templates, MES offers MES templates, and Transplants offers
 * TRANSPLANT templates only. It used to offer "everything but Kit and MES",
 * which put Cleaning, Equipment and Inventory templates under Create transplant
 * — none of them patient care: a cleaning or maintenance run belongs to a room
 * or a machine (Cleaning module, z8nrz7ewt1), and an Inventory run is started
 * by receiving a lot, never by hand. CAPA creates no procedure, so it offers
 * what Transplants does.
 */
export function templatesOfferedFor<T extends { type?: ProcedureType | null }>(
  variant: ProcedureListVariant,
  templates: readonly T[],
): T[] {
  const wanted: ProcedureType =
    variant === 'kit' ? KIT_PROCEDURE_TYPE : variant === 'mes' ? MES_PROCEDURE_TYPE : 'Transplant'
  return templates.filter(t => (t.type ?? 'Transplant') === wanted)
}
